import { isEmpty } from 'lodash'
import { useLocation } from 'react-router-dom'

import { useGetDisasterRecoveryTestPermissions } from 'components/disaster-recovery/hooks/use-get-disaster-recovery-test-permissions'
import { DISASTER_RECOVERY_TESTING_URLS } from 'components/disaster-recovery/utils/disaster-recovery-testing-routes'
import { HOME_PATHS } from 'components/home/HomeLayoutWrapper'
import { type FontAwesomeIconName, type LegacyIconName } from 'components/Icon'
import { useWhatRanAccess } from 'components/what-ran/use-what-ran-access'
import useAuth from 'hooks/use-auth'
import { useManageSharedAssets } from 'hooks/use-manage-shared-assets'
import { trans } from 'i18n/translate'
import { hasSupportAppPermission } from 'support/helpers/support-rbac'
import { isPrivateEdition } from 'utils/app-config'
import { getBaseUrl } from 'utils/baseUrls'
import {
  attacksActiveGet,
  failureFlagsExperimentsActiveGet,
  orgsIdGet,
  reliabilityTestsActiveGet,
  scenariosActiveGet,
  schedulesAttacksPagedGet,
  schedulesScenariosPagedGet,
  schedulesServicesPagedGet,
  servicesGet,
  testSuitesGet
} from 'utils/endpoints-access'

import type { CSSProperties, ReactNode } from 'react'

export type SubNavItemConfig =
  | {
      enabled: boolean
      isSectionHeader: true
      name: string
      text: string
    }
  | {
      disabled?: boolean
      enabled: boolean
      icon?: FontAwesomeIconName | LegacyIconName
      isSectionHeader?: false
      name: string
      route: string
      showNotification?: boolean
      state?: Record<string, any>
      text: string
    }

const REPORTS_SUB_NAV: SubNavItemConfig[] = [
  {
    enabled: true,
    isSectionHeader: true,
    name: 'reports-company-label',
    text: 'Company'
  },
  {
    enabled: true,
    name: 'reliability-report',
    text: 'Reliability Report',
    route: '/reports/company/reliability'
  },
  {
    enabled: true,
    name: 'heatmap',
    text: 'Heatmap',
    route: '/reports/company/heatmap'
  },
  {
    enabled: true,
    name: 'risk-inventory',
    text: 'Risk Inventory',
    route: '/reports/company/risks'
  },
  {
    enabled: true,
    isSectionHeader: true,
    name: 'reports-team-label',
    text: 'Team'
  },
  {
    enabled: true,
    name: 'team-risk',
    text: 'Team Risk',
    route: '/reports/team/risk'
  },
  {
    enabled: true,
    name: 'team-score',
    text: 'Team Score',
    route: '/reports/team/score'
  }
]

export interface NavItemConfig {
  isActive: boolean
  name: string
  disabled: boolean
  href?: string
  icon: FontAwesomeIconName | LegacyIconName
  isExternalLink?: boolean
  text: ReactNode
  route?: string
  state?: Record<string, any>
  props?: Record<string, any>
  iconStyle?: CSSProperties
  showBadgeCount?: boolean
  showNotification?: boolean
  enabled: boolean
  showSeparator?: boolean
  style?: CSSProperties
  subNav?: SubNavItemConfig[]
}

export interface UseGremlinNavigationHook {
  navigationConfigItems: NavItemConfig[]
}

export function useGremlinNavigation({
  userHasTeams
}: {
  userHasTeams: boolean
}): UseGremlinNavigationHook {
  const { pathname: currentPathname } = useLocation()
  const {
    currentTeamId,
    hasAI,
    hasEndpointAccess,
    hasFaultInjection,
    hasFailureFlags,
    hasReliabilityManagement,
    session
  } = useAuth()

  const { pendingIncomingRequestsByAssetId } = useManageSharedAssets()

  const hasPendingClusterAccessRequests = !isEmpty(
    pendingIncomingRequestsByAssetId
  )

  const whatRanAccess = useWhatRanAccess()
  const { canReadDisasterRecoveryTests, canReadDisasterRecoveryTestReports } =
    useGetDisasterRecoveryTestPermissions()

  const disableForOnboarding =
    currentPathname.startsWith('/onboarding') &&
    currentPathname !== '/onboarding/profile'

  const isOnboardingOrHasNoTeams = disableForOnboarding || !userHasTeams

  const accessOptions = { teamId: currentTeamId }

  const canShowNowRunning =
    hasEndpointAccess(attacksActiveGet, accessOptions) ||
    hasEndpointAccess(scenariosActiveGet, accessOptions) ||
    hasEndpointAccess(reliabilityTestsActiveGet, accessOptions) ||
    hasEndpointAccess(failureFlagsExperimentsActiveGet, accessOptions)

  const canShowServices =
    hasReliabilityManagement && hasEndpointAccess(servicesGet, accessOptions)

  const canShowWhatsScheduled =
    hasEndpointAccess(schedulesAttacksPagedGet, accessOptions) ||
    hasEndpointAccess(schedulesScenariosPagedGet, accessOptions) ||
    hasEndpointAccess(schedulesServicesPagedGet, accessOptions)

  const canShowWhatRan = whatRanAccess.section

  const canShowDisasterRecovery =
    canReadDisasterRecoveryTests && canReadDisasterRecoveryTestReports

  // Foresight answers on gremlin.ai, so this url arrives from config alone
  const foresightUrl = getBaseUrl('foresight')

  const navigationConfigItems: NavItemConfig[] = [
    {
      isActive:
        currentPathname.startsWith(HOME_PATHS.TeamReliability) ||
        currentPathname.startsWith(HOME_PATHS.GettingStarted),
      name: 'home',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'house',
      text: 'Home',
      route: HOME_PATHS.TeamReliability,
      props: { 'data-testid': 'data-test-nav-home' },
      enabled: hasReliabilityManagement
    },
    {
      disabled: false,
      enabled: hasAI && !isPrivateEdition() && Boolean(foresightUrl),
      href: foresightUrl,
      icon: 'foresightEyesLight',
      isActive: false,
      isExternalLink: true,
      name: 'foresightAI',
      text: 'Foresight AI',
      showSeparator: true
    },
    {
      isActive: currentPathname.startsWith('/reports'),
      name: 'reports',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartLineUp',
      text: 'Reports',
      props: { 'data-test-nav-company-dashboard': true },
      enabled: hasReliabilityManagement,
      subNav: REPORTS_SUB_NAV
    },
    {
      isActive: currentPathname.startsWith('/now-running'),
      name: 'now-running',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'regularArrowsRotate',
      text: 'Now Running',
      route: '/now-running',
      showBadgeCount: true,
      props: { 'data-test-nav-now-running': true },
      enabled: canShowNowRunning
    },
    {
      isActive: currentPathname.startsWith('/schedules'),
      name: 'whats-scheduled',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'calendar',
      text: "What's Scheduled",
      route: '/schedules',
      props: { 'data-test-nav-scheduled-new': true },
      enabled: canShowWhatsScheduled
    },
    {
      isActive: currentPathname.startsWith('/run-history'),
      name: 'run-history',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'rectangleHistory',
      text: 'What Ran',
      route: '/run-history',
      props: { 'data-testid': 'nav-run-history' },
      enabled: canShowWhatRan
    },
    {
      isActive: currentPathname.startsWith('/services'),
      name: 'services',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartNetwork',
      text: 'Services',
      route: '/services',
      state: {
        clearState: true
      },
      props: { 'data-test-nav-services': true },
      enabled: canShowServices
    },
    {
      isActive:
        currentPathname.startsWith('/attacks') ||
        currentPathname.startsWith('/alfi'),
      name: 'attacks',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'flask',
      text: 'Experiments',
      route: '/attacks',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/scenarios'),
      name: 'scenarios',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'clipboardList',
      text: 'Scenarios',
      route: '/scenarios/my-scenarios',
      state: {
        clearSearchState: true
      },
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/failure-flags'),
      name: 'failure-flags',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'flag',
      text: trans('failure_flags.label.plural'),
      route: '/failure-flags/list',
      props: { 'data-test-nav-failure-flags': true },
      enabled: hasFailureFlags
    },
    {
      isActive: currentPathname.startsWith(
        DISASTER_RECOVERY_TESTING_URLS.base()
      ),
      name: 'disaster-recovery-tests',
      disabled: !canShowDisasterRecovery || isOnboardingOrHasNoTeams,
      icon: 'fireExtinguisher',
      text: 'Disaster Recovery',
      route: DISASTER_RECOVERY_TESTING_URLS.base(),
      enabled: canShowDisasterRecovery && hasReliabilityManagement
    },
    {
      isActive: currentPathname.startsWith('/gamedays'),
      name: 'gamedays',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'joystick',
      text: 'GameDays',
      route: '/gamedays',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/configurations'),
      name: 'configurations',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'sliders',
      text: 'Configurations',
      showNotification: hasPendingClusterAccessRequests,
      enabled: true,
      subNav: [
        {
          enabled: hasReliabilityManagement && hasEndpointAccess(testSuitesGet),
          name: 'test-suites',
          text: 'Test Suites',
          route: '/configurations/test-suites',
          icon: 'books'
        },
        {
          enabled: true,
          name: 'status-checks',
          text: 'Health Checks',
          route: '/configurations/health-checks/list',
          icon: 'wavePulse'
        },
        {
          enabled: hasEndpointAccess(orgsIdGet),
          name: 'agents',
          text: 'Agents',
          route: '/configurations/agents',
          icon: 'clients'
        },
        {
          enabled: true,
          name: 'clusters',
          text: 'Clusters',
          route: '/configurations/clusters',
          icon: 'dharmachakra',
          showNotification: hasPendingClusterAccessRequests
        },
        {
          enabled: true,
          name: 'pni',
          text: 'PNI',
          route: '/configurations/private-network-integrations',
          icon: 'lock'
        }
      ]
    },
    {
      isActive: currentPathname === '/help',
      name: 'help',
      disabled: false,
      icon: 'messageQuestion',
      text: 'Help',
      route: '/help',
      enabled: true,
      showSeparator: true
    },
    {
      isActive:
        currentPathname.startsWith('/support/') ||
        currentPathname.startsWith('/supportnew/'),
      name: 'support',
      disabled: false,
      icon: 'support',
      text: 'Support App',
      route: '/support',
      enabled: hasSupportAppPermission(session)
    }
  ]

  return {
    navigationConfigItems
  }
}
